import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsAPI } from '../../api/client';
import TestHistoryList from './TestHistoryList';
import TestResults from '../Test/TestResults';
import Loader from '../Common/Loader';
import ErrorMessage from '../Common/ErrorMessage';
import {
    trackTestDetailsPageView,
    trackTestHistoryClick,
} from '../../utils/analytics';

const TestDetailsPage = () => {
    const { endpointId } = useParams();
    const navigate = useNavigate();
    const [endpoint, setEndpoint] = useState(null);
    const [tests, setTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showHistory, setShowHistory] = useState(false);

    const fetchTestData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const response = await testsAPI.getByEndpoint(endpointId, currentPage, 10);
            setEndpoint(response.data.endpoint);
            setTests(response.data.tests);
            setTotalPages(response.data.pagination.pages);

            // Set selected test to latest by default  
            if (!selectedTest && response.data.latestTest) {
                setSelectedTest(response.data.latestTest);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load test data');
        } finally {
            setLoading(false);
        }
    }, [endpointId, currentPage, selectedTest]);

    useEffect(() => {
        fetchTestData();
    }, [fetchTestData]);

    useEffect(() => {
        trackTestDetailsPageView(endpointId);
    }, [endpointId]);

    const handleTestSelect = (test) => {
        trackTestHistoryClick(test._id);
        setSelectedTest(test);
        setShowHistory(false);
    };

    const handleBackToProject = () => {
        if (endpoint && endpoint.projectId) {
            navigate(`/project/${endpoint.projectId}`);
        } else {
            navigate('/dashboard');
        }
    };

    if (loading) {
        return <Loader message="Loading test details..." />;
    }

    if (error) {
        return <ErrorMessage message={error} onRetry={fetchTestData} />;
    }

    if (!endpoint) {
        return <ErrorMessage message="Endpoint not found" />;
    }

    return (
        <div className="fade-in">
            <style>{`
        .details-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
          gap: 16px;
        }
        
        .details-title {
          font-family: "Agrandir Narrow", "Agrandir", Arial, sans-serif;
          font-size: 36px;
          font-weight: 700;
          font-style: normal;
          margin-bottom: 10px;
          color: var(--text-primary);
        }
        
        .endpoint-info {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-top: 10px;
          flex-wrap: wrap;
        }
        
        .endpoint-url {
          font-family: monospace;
          font-size: 14px;
          color: var(--text-secondary);
          word-break: break-all;
        }
        
        .details-layout {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 30px;
        }
        
        .details-sidebar {
          position: sticky;
          top: 100px;
          align-self: start;
        }
        
        .sidebar-title {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 8px;
          color: var(--text-primary);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .sidebar-subtitle {
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 20px;
        }
        
        .main-content {
          min-height: 400px;
        }
        
        .test-info-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          margin-bottom: 20px;
          gap: 16px;
          flex-wrap: wrap;
        }
        
        .test-date {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 5px;
        }
        
        .mobile-history-toggle {
          display: none;
          margin-bottom: 20px;
        }
        
        .mobile-history-panel {
          display: none;
        }
        
        @media (max-width: 1024px) {
          .details-layout {
            grid-template-columns: 280px 1fr;
            gap: 20px;
          }
        }
        
        @media (max-width: 768px) {
          .details-header {
            flex-direction: column;
            align-items: stretch;
          }
          
          .details-title {
            font-size: 24px;
          }
          
          .details-header .btn-primary {
            width: 100%;
            text-align: center;
          }
          
          .details-layout {
            display: block;
          }
          
          .details-sidebar {
            display: none;
          }
          
          .mobile-history-toggle {
            display: block;
          }
          
          .mobile-history-panel {
            display: ${showHistory ? 'block' : 'none'};
            margin-bottom: 20px;
          }
          
          .mobile-history-panel.open {
            display: block;
          }
          
          .test-info-bar {
            flex-direction: column;
            text-align: center;
          }
        }
        
        @media (max-width: 480px) {
          .details-title {
            font-size: 20px;
          }
          
          .endpoint-url {
            font-size: 12px;
          }
        }
      `}</style>

            <div className="details-header">
                <div>
                    <button
                        className="btn btn-secondary btn-small"
                        onClick={handleBackToProject}
                        style={{ marginBottom: '10px' }}
                    >
                        ← Back to Project
                    </button>
                    <h1 className="details-title">Endpoint Test Details</h1>
                    <div className="endpoint-info">
                        <span
                            className="badge"
                            style={{
                                background: 'rgba(0, 0, 0, 0.08)',
                                color: 'var(--text-primary)',
                            }}
                        >
                            {endpoint.method}
                        </span>
                        <span className="endpoint-url">{endpoint.url}</span>
                    </div>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/project/${endpoint.projectId}/test/new`)}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    + New Test
                </button>
            </div>

            {/* Mobile History Toggle */}
            <div className="mobile-history-toggle">
                <button
                    className="btn btn-secondary"
                    onClick={() => setShowHistory(!showHistory)}
                    style={{ width: '100%' }}
                >
                    {showHistory ? '✕ Hide Test History' : '📋 Show Test History'} ({endpoint.totalTests} tests)
                </button>
            </div>

            {/* Mobile History Panel */}
            <div className={`mobile-history-panel ${showHistory ? 'open' : ''}`} style={{ display: showHistory ? 'block' : 'none' }}>
                <div className="card">
                    <h3 className="sidebar-title">Test History</h3>
                    <p className="sidebar-subtitle">
                        Total Tests: <strong>{endpoint.totalTests}</strong>
                    </p>
                    <TestHistoryList
                        tests={tests}
                        selectedTestId={selectedTest?._id}
                        onSelect={handleTestSelect}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>

            <div className="details-layout">
                {/* Test History Sidebar - Desktop Only */}
                <div className="details-sidebar">
                    <div className="card">
                        <h3 className="sidebar-title">Test History</h3>
                        <p className="sidebar-subtitle">
                            Total Tests: <strong>{endpoint.totalTests}</strong>
                        </p>
                        <TestHistoryList
                            tests={tests}
                            selectedTestId={selectedTest?._id}
                            onSelect={handleTestSelect}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>

                {/* Test Results Main Area */}
                <div className="main-content">
                    {selectedTest && selectedTest.status === 'completed' ? (
                        <>
                            <div className="test-info-bar">
                                <div>
                                    <h3 className="test-date">
                                        Test Run: {new Date(selectedTest.createdAt).toLocaleString()}
                                    </h3>
                                </div>
                                <div>
                                    <span className="badge badge-success">Completed</span>
                                </div>
                            </div>
                            <TestResults
                                results={selectedTest.results}
                                testId={selectedTest._id}
                                onRunAnother={() => navigate(`/project/${endpoint.projectId}/test/new`)}
                                onBackToProject={handleBackToProject}
                            />
                        </>
                    ) : selectedTest && selectedTest.status === 'failed' ? (
                        <div className="card">
                            <div className="error-message">
                                <span>⚠️</span>
                                <div>
                                    <h3>Test Failed</h3>
                                    <p>{selectedTest.error || 'Unknown error occurred'}</p>
                                </div>
                            </div>
                        </div>
                    ) : selectedTest && selectedTest.status === 'running' ? (
                        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <div className="loader" style={{ margin: '0 auto 20px' }}></div>
                            <h3>Test is still running...</h3>
                            <p style={{ color: 'var(--text-secondary)' }}>
                                Refresh the page to see updated results
                            </p>
                            <button
                                className="btn btn-secondary"
                                onClick={fetchTestData}
                                style={{ marginTop: '20px', width: 'auto' }}
                            >
                                Refresh
                            </button>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-state-icon">📊</div>
                            <h3>No test selected</h3>
                            <p>Select a test from the history to view results</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TestDetailsPage;
